import React from 'react'
import type { PQueryBuilderField } from '@pushly/aqe/lib/components/pquery-builder'

export interface EventPropertyContextValue {
    /**
     * The event type's registered properties, as the catalog entries they are addressed by.
     *
     * Empty until an event type has been chosen, and empty again when the chosen type is one the
     * registry no longer holds — a condition already bound to one of its properties still shows the
     * path it names, so the reference the journey depends on stays legible rather than disappearing.
     */
    properties: PQueryBuilderField[]
}

const EMPTY: EventPropertyContextValue = { properties: [] }

/**
 * The event type's property map, reaching the value editors mounted inside a condition builder.
 *
 * A value input is resolved by the builder from a registry and is handed only the condition it
 * edits, so anything about the surrounding journey — which event type its trigger names, what that
 * type declares — has to arrive through context.
 */
export const EventPropertyContext = React.createContext<EventPropertyContextValue>(EMPTY)

export function useEventProperties(): PQueryBuilderField[] {
    return React.useContext(EventPropertyContext).properties
}

export const EventPropertyProvider: React.FC<{
    properties: PQueryBuilderField[]
    children: React.ReactNode
}> = ({ properties, children }) => {
    const value = React.useMemo<EventPropertyContextValue>(() => ({ properties }), [properties])

    return <EventPropertyContext.Provider value={value}>{children}</EventPropertyContext.Provider>
}
